/*
 * Refonte — petits comportements globaux, sans librairie :
 * 1. apparitions discrètes au défilement (seulement si <html> a la classe rf-anim) ;
 * 2. essai des initiales dans le hero (titre doré + filigrane en direct) ;
 * 3. halo lumineux qui suit le pointeur sur les cartes (souris uniquement) ;
 * 4. « dorure à chaud » : titres découpés en lettres qui se posent, bouton d'achat qui lance
 *    un reflet au clic, bandeau qui accélère avec la vitesse de défilement.
 */
(function () {
  const root = document.documentElement;

  /* 1. Apparitions au défilement */
  function initReveal() {
    if (!root.classList.contains('rf-anim')) return;
    if (!('IntersectionObserver' in window)) {
      root.classList.remove('rf-anim');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    const observe = (scope) => {
      scope.querySelectorAll('[data-rf-reveal]:not(.is-visible)').forEach((el) => observer.observe(el));
    };
    observe(document);
    // Éditeur de thème : sections rechargées à chaud
    document.addEventListener('shopify:section:load', (event) => observe(event.target));
  }

  /* 2. Essai des initiales dans le hero */
  function initHeroTry(scope) {
    scope.querySelectorAll('.rf-hero__try-input').forEach((input) => {
      if (input.dataset.rfBound) return;
      input.dataset.rfBound = 'true';
      const hero = input.closest('.rf-hero');
      const live = hero?.querySelector('.rf-hero__live');
      const watermark = hero?.querySelector('.rf-hero__watermark');
      const allowed = new Set(Array.from(input.dataset.allowed || ''));
      const max = Number(input.maxLength) > 0 ? Number(input.maxLength) : 4;

      let previous = '';
      input.addEventListener('input', () => {
        // On retire en silence les caractères qui ne peuvent pas être gravés.
        const clean = Array.from(input.value).filter((c) => allowed.has(c)).slice(0, max).join('');
        if (clean !== input.value) input.value = clean;
        if (live) {
          if (clean) {
            // Une lettre par élément : seules les nouvelles lettres sont « frappées ».
            const before = Array.from(previous);
            live.replaceChildren(
              ...Array.from(clean).map((char, i) => {
                const span = document.createElement('span');
                span.className = char === before[i] ? 'rf-live-ch' : 'rf-live-ch is-hit';
                span.textContent = char;
                return span;
              })
            );
          } else {
            live.textContent = live.dataset.default || '';
          }
        }
        if (watermark) watermark.textContent = clean || watermark.dataset.default || '';
        previous = clean;
      });
    });
  }

  /* 3. Halo qui suit le pointeur sur les cartes, et inclinaison 3D (6° max) */
  function initSpotlight() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const tilt = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    /** @type {HTMLElement | null} */
    let current = null;
    const release = () => {
      current?.classList.remove('is-tilting');
      current = null;
    };
    document.addEventListener(
      'pointermove',
      (event) => {
        const card = event.target instanceof Element ? event.target.closest('.rf-card') : null;
        if (card !== current) release();
        if (!(card instanceof HTMLElement)) return;
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        card.style.setProperty('--mx', `${x}px`);
        card.style.setProperty('--my', `${y}px`);
        // On n'incline qu'une carte déjà apparue, pour ne pas gêner son animation d'entrée.
        if (!tilt || (card.hasAttribute('data-rf-reveal') && !card.classList.contains('is-visible'))) return;
        card.style.setProperty('--ry', `${((x / rect.width) - 0.5) * 12}deg`);
        card.style.setProperty('--rx', `${(0.5 - y / rect.height) * 12}deg`);
        card.classList.add('is-tilting');
        current = card;
      },
      { passive: true }
    );
    document.addEventListener('pointerleave', release);
  }

  /* 4a. Titres : découpés en lettres qui se posent comme un tampon (CSS .rf-stamp).
     Le mot doré reste d'un seul bloc pour garder son dégradé et recevoir le reflet. */
  function initStamp(scope) {
    if (!root.classList.contains('rf-anim')) return;
    scope.querySelectorAll('.rf-title[data-rf-reveal]:not(.rf-stamp)').forEach((title) => {
      // Les lecteurs d'écran lisent le titre entier, pas une lettre à la fois.
      title.setAttribute('aria-label', title.textContent.replace(/\s+/g, ' ').trim());
      let index = 0;
      const walk = (node) => {
        for (const child of Array.from(node.childNodes)) {
          if (child.nodeType === Node.TEXT_NODE) {
            const fragment = document.createDocumentFragment();
            for (const part of child.textContent.split(/(\s+)/)) {
              if (!part) continue;
              if (/^\s+$/.test(part)) {
                fragment.append(document.createTextNode(part));
                continue;
              }
              const word = document.createElement('span');
              word.className = 'rf-word';
              word.setAttribute('aria-hidden', 'true');
              for (const char of Array.from(part)) {
                const span = document.createElement('span');
                span.className = 'rf-ch';
                span.style.setProperty('--i', String(index++));
                span.textContent = char;
                word.append(span);
              }
              fragment.append(word);
            }
            child.replaceWith(fragment);
          } else if (child instanceof HTMLElement && child.classList.contains('rf-gold-text')) {
            child.classList.add('rf-ch');
            child.setAttribute('aria-hidden', 'true');
            child.style.setProperty('--i', String(index));
            index += 3;
          } else if (child instanceof HTMLElement && child.tagName !== 'BR') {
            walk(child);
          }
        }
      };
      walk(title);
      title.style.setProperty('--rf-chars', String(index));
      title.classList.add('rf-stamp');
    });
  }

  /* 4b. Bouton d'achat : un reflet part dès le clic (en plus du reflet en boucle). */
  function initButtonFlash() {
    if (!root.classList.contains('rf-anim')) return;
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('.add-to-cart-button.button') : null;
        if (!(button instanceof HTMLElement) || button.dataset.rfLocked === 'true') return;
        button.classList.remove('rf-flash');
        void button.offsetWidth;
        button.classList.add('rf-flash');
        window.setTimeout(() => button.classList.remove('rf-flash'), 650);
      },
      true
    );
  }

  /* 4c. Bandeau : accélère jusqu'à ×3 quand on défile vite, puis revient à ×1 en ~600 ms. */
  function initMarqueeBoost() {
    if (!root.classList.contains('rf-anim') || !('getAnimations' in Element.prototype)) return;
    let lastY = 0;
    let lastT = 0;
    let boost = 1;
    let frame = 0;
    let lastFrame = 0;
    const apply = () => {
      document.querySelectorAll('.rf-marquee__track').forEach((track) => {
        track.getAnimations().forEach((animation) => (animation.playbackRate = boost));
      });
    };
    const settle = (now) => {
      const dt = Math.min(64, now - lastFrame || 16);
      lastFrame = now;
      boost = 1 + (boost - 1) * Math.exp(-dt / 200);
      if (boost < 1.01) boost = 1;
      apply();
      frame = boost > 1 ? requestAnimationFrame(settle) : 0;
    };
    // Capture : sur ordinateur, le thème fait défiler .page-wrapper et non la fenêtre.
    document.addEventListener(
      'scroll',
      (event) => {
        const scroller = event.target instanceof Element ? event.target : document.scrollingElement;
        if (!scroller) return;
        const now = performance.now();
        const y = scroller.scrollTop;
        const dt = now - lastT;
        if (dt > 0 && dt < 200) {
          const speed = Math.abs(y - lastY) / dt; // pixels par milliseconde
          boost = Math.max(boost, 1 + Math.min(2, speed * 1.5));
        }
        lastY = y;
        lastT = now;
        if (!frame && boost > 1) {
          lastFrame = now;
          frame = requestAnimationFrame(settle);
        }
      },
      { capture: true, passive: true }
    );
  }

  // Le découpage des titres doit précéder l'observateur d'apparition.
  initStamp(document);
  initReveal();
  initHeroTry(document);
  initSpotlight();
  initButtonFlash();
  initMarqueeBoost();
  document.addEventListener('shopify:section:load', (event) => {
    initStamp(event.target);
    initHeroTry(event.target);
  });
})();
